import * as angular from 'angular';
import { IController } from 'angular';
import * as _ from 'lodash';
import { UtilitiesService } from 'src/ng2/shared/services/utilities/utilities.service';
import { CreditRequirements } from '../../../../../ng2/shared/constants/credit-requirements.constant';
import { NextRegentsAdminDate } from '../../../../../ng2/shared/constants/next-regents-admin-date.constant';
import { ICreditArea, IStudent } from '../../../../../ng2/shared/typings/interfaces/student.interface';
import { ImStudent } from './../../../../../ng2/shared/services/im-models/im-student.service';
import { ImStudentCreditGaps } from './../../../../shared/static-models/im-models/im-student-credit-gaps';

interface ITableCreditArea extends ICreditArea {
  key: string;
  humanName: string;
  order: number;
}

interface ITableRow {
  gradReq: string;
  earned: number;
  behind: number;
  earnedAndSched: number;
  neededForOnTrack: string;
  neededForOnTrackPriorTerm: string;
  gap: number;
  gradReqVal: string;
  regentsPlanned: string[];
}

class StudentCreditsPanelTableCtrl implements IController {
  static $inject = ['UtilitiesService', '$scope', 'ImStudentRegents', 'ImStudentCreditGaps', 'ImStudent'];

  // panel title
  public title = 'Credits';
  public nextRegentsAdminDate = NextRegentsAdminDate;
  public table;

  // bindings
  private student: IStudent;
  private school;
  private isStudentInFirstTerm: boolean;

  constructor(
    private UtilitiesService: UtilitiesService,
    private $scope,
    private ImStudentRegents,
    private imStudentCreditGaps: ImStudentCreditGaps,
    private imStudent: ImStudent,
  ) {}

  $onInit() {
    // table in html:
    this.table = this.buildTable(this.student, this.school);

    this.$scope.$watch(() => this.student.nextScheduledRegents, () => this._buildRows, true);
  }

  buildTable(student, school) {
    const byArea = this.student.creditDetails.byArea;
    const areasToOmit = ['ssGovtEcon', 'hpe', 'elaOther', 'sciCore', 'sciOther', 'ssElective', 'misc'];
    const creditDetails: ICreditArea = _.omit(byArea, areasToOmit);
    const creditDetailsByExam: ITableCreditArea[] = this._getCreditDetailsByExam(creditDetails);
    const onTrackCreditReqs = this.imStudentCreditGaps.getStudentOnTrackMetrics(student, school);
    const onTrackCreditReqsForPriorTerm = this.imStudentCreditGaps.getStudentOnTrackMetricsForPriorTerm(
      student,
      school,
    );
    const studentCurrentTerm = this.imStudent.getStudentCurrentTerm(student, school);
    this.isStudentInFirstTerm = studentCurrentTerm <= 1;

    const creditReqs = this.imStudentCreditGaps.getGradReqsForStudent(student, onTrackCreditReqs);
    const rows = this._buildRows(creditDetailsByExam, onTrackCreditReqs, onTrackCreditReqsForPriorTerm, creditReqs);
    const table = { rows };
    return table;
  }

  _getScheduledRegentsExamsForGradReq(gradReq, adminDate): string[] {
    const regentsExams = this.ImStudentRegents.getScheduledRegentsExamsForGradReq(this.student, gradReq, adminDate);
    const regentsExamNames: string[] = _.map(regentsExams, 'humanName');
    return regentsExamNames;
  }

  _getCreditDetailsByExam(_creditDetails: ICreditArea): ITableCreditArea[] {
    const creditDetails = angular.copy(_creditDetails);
    const mappedToCreditAreaKey = [];

    // Lodash + Typescript does not like _.map, _.filter, etc with interfaces. Seems like a bug
    // Using forEach instead
    _.forEach(creditDetails, (value, key: string) => {
      const creditReq = _.find(CreditRequirements, { camelCase: key });
      const creditDetail: ITableCreditArea = _.assign(
        {
          key,
          humanName: creditReq.human,
          order: creditReq.orders.creditPanel,
        },
        value,
      );

      mappedToCreditAreaKey.push(creditDetail);
    });
    const sorted = _.sortBy(mappedToCreditAreaKey, 'order');
    return sorted;
  }

  _buildRows(
    creditDetailsByExam: ITableCreditArea[],
    onTrackCreditReqs,
    onTrackCreditReqsForPriorTerm,
    creditReqs,
  ): ITableRow[] {
    const rows = _.map(creditDetailsByExam, creditReq => {
      const gradReq = creditReq.humanName;
      const gap = this.imStudentCreditGaps.getStartGapForGradReq(this.student, creditReq.key);
      const earned = creditReq.earned;
      const behind = creditReq.creditsBehind * -1;
      const earnedAndSched = creditReq.earnedPlusScheduled;
      const neededForOnTrack = onTrackCreditReqs ? onTrackCreditReqs[creditReq.key] : null;

      let neededForOnTrackPriorTerm;
      if (this.isStudentInFirstTerm) {
        neededForOnTrackPriorTerm = 0;
      } else {
        neededForOnTrackPriorTerm = onTrackCreditReqsForPriorTerm ? onTrackCreditReqsForPriorTerm[creditReq.key] : null;
      }

      const gradReqVal = creditReqs ? creditReqs[creditReq.key].gradReq : null;
      const regentsPlanned = this._getScheduledRegentsExamsForGradReq(creditReq.key, NextRegentsAdminDate);

      const row = {
        gradReq,
        gap,
        earned,
        behind,
        earnedAndSched,
        neededForOnTrack,
        neededForOnTrackPriorTerm,
        gradReqVal,
        regentsPlanned,
      };

      return row;
    });
    return rows;
  }
}

angular.module('nv.singleStudent').controller('StudentCreditsPanelCtrl', StudentCreditsPanelTableCtrl);
